import type React from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import { Button, Text } from '@change/ds-core-ui';
import { UploadIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { AccessibleFileInputWrapper } from './AccessibleFileInputWrapper';

type Props = {
	disabled?: boolean;
	onButtonClick?: () => void;
	uppy: Uppy<Meta, Body>;
	fileInputId?: string;
	inputDataQa?: string;
	/** Tailwind classes for the dashed dropzone container (height, width, centering). Defaults to `h-[272px]` full width. */
	containerClassName?: string;
};

export const ImageUploader: React.FC<Props> = ({
	disabled = false,
	onButtonClick,
	uppy,
	fileInputId = 'file-input-id',
	inputDataQa,
	containerClassName = 'h-[272px]',
}) => {
	const { translate } = useI18n();
	const uploadAPhotoText = { strings: { chooseFiles: translate('corgi.components.image_upload.button') } };

	return (
		<div className={cn('image-uploader', containerClassName)}>
			<div className="border-stroke-strong h-full rounded-lg border border-dashed">
				<div className="media-upload-content-wrapper flex h-full flex-col items-center justify-center gap-4">
					<div className="hidden leading-[0] md:block">
						<UploadIcon data-testid="drop-target" size={48} className="text-icon-weak h-12 w-12" />
					</div>
					<div id="sap-media-view">
						<span className="sr-only">
							<Translate value="corgi.sap.media.a11y.uploader.accessible_hidden_div" />
						</span>
						{!disabled ? (
							<AccessibleFileInputWrapper
								uploadButtonText={uploadAPhotoText.strings.chooseFiles}
								uppy={uppy}
								fileInputId={fileInputId}
								inputDataQa={inputDataQa}
								onButtonClick={onButtonClick}
							/>
						) : (
							<Button disabled={disabled} data-qa="media-images-upload" variant="secondary" size="small">
								<Text size="small">{uploadAPhotoText.strings.chooseFiles}</Text>
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

ImageUploader.displayName = 'ImageUploader';
